import {
  Component,
  OnDestroy,
  OnInit,
  effect,
  input,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { EnvPrefixPipe } from '../../pipes/env-prefix.pipe';
import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'app-startup-loader',
  standalone: true,
  imports: [IconComponent, EnvPrefixPipe],
  templateUrl: './startup-loader.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './startup-loader.component.css',
})
export class StartupLoaderComponent implements OnInit, OnDestroy {
  // Allow parent to override message (e.g., "Syncing your data...")
  message = input<string | null>(null);

  protected readonly currentMessage = signal('');
  protected readonly messages = [
    'Waking up the hamsters...',
    'Warming up the database...',
    'Loading excuses for slow performance...',
    'Analyzing your attitude...',
    "Checking for updates we'll never install...",
    'Pretending to work hard...',
    'Consulting the magic 8-ball...',
    'Reticulating splines...',
    'Herding cats...',
    'Teaching AI to be polite...',
    'Counting backwards from infinity...',
    'Sorting random numbers...',
    'Mining for data...',
    'Charging flux capacitor...',
    'Calibrating reality distortion field...',
    'Initializing coffee machine...',
    'Summoning digital spirits...',
    'Untangling Christmas lights...',
  ];

  private shuffledMessages: string[] = [];
  private messageIndex = 0;
  private intervalId?: number;
  private progressIntervalId?: number;

  constructor() {
    // Watch for message input changes and update display
    effect(() => {
      const customMessage = this.message();
      if (customMessage) {
        // Stop any existing intervals
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = undefined;
        }
        if (this.progressIntervalId) {
          clearInterval(this.progressIntervalId);
          this.progressIntervalId = undefined;
        }

        // Start progressive message display
        this.startProgressiveMessages(customMessage);
      } else if (!this.intervalId && this.shuffledMessages.length > 0) {
        // Resume cycling if custom message is cleared
        this.startCycling();
      }
    });
  }

  ngOnInit(): void {
    // If custom message provided initially, use it with progressive display
    if (this.message()) {
      return; // effect() will handle it
    }

    this.startCycling();
  }

  private startProgressiveMessages(syncMessage: string): void {
    // Create initial mix: 30% sync messages, 70% fun messages
    const funMessages = [...this.messages].sort(() => Math.random() - 0.5);
    const totalMessages = 20; // Total pool size
    const initialSyncCount = Math.floor(totalMessages * 0.3);
    const funCount = totalMessages - initialSyncCount;

    // Build initial message pool
    this.shuffledMessages = [
      ...Array(initialSyncCount).fill(syncMessage),
      ...funMessages.slice(0, funCount),
    ].sort(() => Math.random() - 0.5);

    this.messageIndex = 0;
    this.currentMessage.set(this.shuffledMessages[0]);

    // Cycle through messages every 600ms (faster pace)
    this.intervalId = window.setInterval(() => {
      this.messageIndex = (this.messageIndex + 1) % this.shuffledMessages.length;
      this.currentMessage.set(this.shuffledMessages[this.messageIndex]);
    }, 600);

    // Every 3 seconds, replace 2 fun messages with sync messages
    // Over 15s: 4 intervals × 2 replacements = all messages converted by 12s
    // This ensures last 3 seconds show only "Syncing your data..."
    this.progressIntervalId = window.setInterval(() => {
      // Find fun message indices
      const funIndices = this.shuffledMessages
        .map((msg, idx) => (msg !== syncMessage ? idx : -1))
        .filter((idx) => idx !== -1);

      if (funIndices.length > 0) {
        // Replace up to 2 fun messages with sync message
        const toReplace = Math.min(2, funIndices.length);
        for (let i = 0; i < toReplace; i++) {
          const randomFunIdx = funIndices[Math.floor(Math.random() * funIndices.length)];
          this.shuffledMessages[randomFunIdx] = syncMessage;
          funIndices.splice(funIndices.indexOf(randomFunIdx), 1);
        }
      } else {
        // All messages are now sync messages, stop the progress interval
        if (this.progressIntervalId) {
          clearInterval(this.progressIntervalId);
          this.progressIntervalId = undefined;
        }
      }
    }, 3000);
  }

  private startCycling(): void {
    // Shuffle messages for random order
    this.shuffledMessages = [...this.messages].sort(() => Math.random() - 0.5);

    // Show first message immediately
    this.currentMessage.set(this.shuffledMessages[0]);

    // Cycle through shuffled messages every 800ms
    this.intervalId = window.setInterval(() => {
      this.messageIndex = (this.messageIndex + 1) % this.shuffledMessages.length;
      this.currentMessage.set(this.shuffledMessages[this.messageIndex]);
    }, 800);
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.progressIntervalId) {
      clearInterval(this.progressIntervalId);
    }
  }
}
